import type { IDBPDatabase, DBSchema } from 'idb';
import { openDB } from 'idb';
import isEqual from 'lodash/isEqual';

import { type Schema as PMSchema } from '@atlaskit/editor-prosemirror/model';
import type { StepJson } from '@atlaskit/editor-common/collab';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { Step } from '@atlaskit/editor-prosemirror/transform';
import { AnalyticsStep, LinkMetaStep } from '@atlaskit/adf-schema/steps';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { ACTION } from '@atlaskit/editor-common/analytics';

import { editorOfflineStoragePluginKey } from './pm-plugins/main';
import { SynchronisationLock } from './syncLock';
import type { EditorOfflineStoragePlugin } from './editorOfflineStoragePluginType';
import { fireOfflineStorageAnalyticsEvent } from './analytics';
const OFFLINE_STEPS_STORE_NAME = 'steps';
const OFFLINE_META_STORE_NAME = 'meta';

interface StepsDBSchema extends DBSchema {
	steps: {
		key: number;
		value: {
			step: StepJson;
		};
	};
	meta: {
		key: number;
		value: {
			doc: JSONDocNode;
			version: number;
		};
	};
}

type GetDataResponse = {
	steps: Step[];
	docJSON: JSONDocNode | null;
	version: number | null;
};

export class StepsDatabase {
	private dbPromise: Promise<IDBPDatabase<StepsDBSchema>> | undefined;
	private contentId: string | undefined;
	private syncLock = new SynchronisationLock();
	private api: ExtractInjectionAPI<EditorOfflineStoragePlugin> | undefined;
	private initAttempted = false;

	constructor(api: ExtractInjectionAPI<EditorOfflineStoragePlugin> | undefined) {
		this.api = api;
	}

	private onError(_id: string, error: unknown) {
		// Indicate the steps database is not healthy.
		this.api?.core.actions.execute(({ tr }) =>
			tr.setMeta(editorOfflineStoragePluginKey, {
				healthy: false,
			}),
		);

		const parseErrorMessage = () => {
			if (!error) {
				return 'Unknown error';
			}
			if (error instanceof Error) {
				return error.message;
			}
			return String(error);
		};

		fireOfflineStorageAnalyticsEvent({
			api: this.api,
			action: ACTION.OFFLINE_STORAGE_STEPS_DATABASE_ERROR,
			errorMessage: parseErrorMessage(),
			errorSource: _id,
			errorCause: {
				contentIdPresent: !!this.contentId,
				stepsDatabaseInitAttempted: this.initAttempted,
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				isSSR: !!process.env.REACT_SSR,
			},
		});
	}
	/**
	 * The name of the database for the current contentId
	 */
	public get dbName(): string | undefined {
		if (!this.contentId) {
			return undefined;
		}
		return `editor:offline-steps-${this.contentId}`;
	}

	public get hasLock(): boolean {
		return this.syncLock.hasLock;
	}

	public releaseSyncLock() {
		this.syncLock.releaseLock?.();
	}

	public acquireSyncLock(
		callback?: (granted?: boolean) => Promise<void>,
		waitUntilAvailable = false,
	): void {
		this.syncLock.acquireLock(callback, waitUntilAvailable).catch((e) => {
			this.onError('acquireSyncLock()', e);
		});
	}

	/**
	 * Initialize the class for the given contentId.
	 * Can be re-initialized with a different contentId, which will close an existing connection.
	 * Does not open the database connection.
	 * @param contentId The unique page contentId
	 * @returns true if initialized successfully, false if indexedDB is not supported.
	 */
	async init(contentId: string): Promise<boolean> {
		this.initAttempted = true;

		if (window?.indexedDB === undefined) {
			this.onError('init()', new Error('indexedDB is not defined'));
			return false;
		}

		if (this.contentId === contentId && this.dbPromise) {
			return true;
		}

		if (this.dbPromise) {
			const db = await this.dbPromise;
			db.close();
			this.dbPromise = undefined;
		}

		this.contentId = contentId;
		if (this.dbName) {
			this.syncLock.init(this.dbName);
		}
		return true;
	}

	/**
	 * Opens a new database connection for the current contentId if a connection is not already open.
	 * Otherwise, returns the existing connection.
	 * @returns A promise that resolves to the database connection.
	 */
	private async openDB(): Promise<IDBPDatabase<StepsDBSchema>> {
		if (this.dbName === undefined) {
			throw new Error(
				'StepsDatabase not initialized. Possibly missing contentId, init hasnt been called or rendering in SSR mode.',
			);
		}

		if (this.dbPromise === undefined) {
			this.dbPromise = openDB<StepsDBSchema>(this.dbName, 2, {
				upgrade(db) {
					if (!db.objectStoreNames.contains(OFFLINE_STEPS_STORE_NAME)) {
						db.createObjectStore(OFFLINE_STEPS_STORE_NAME, { keyPath: 'id', autoIncrement: true });
					}
					if (!db.objectStoreNames.contains(OFFLINE_META_STORE_NAME)) {
						db.createObjectStore(OFFLINE_META_STORE_NAME, {});
					}
				},
				terminated: () => {
					this.dbPromise = undefined;
				},
			});
		}

		return this.dbPromise;
	}

	private sanitizeStep(step: Step): any {
		if (step instanceof AnalyticsStep || step instanceof LinkMetaStep) {
			return {
				stepType: 'replace',
				from: 0,
				to: 0,
			};
		}
		return step.toJSON();
	}

	private filterEmptyReplaceStep(step: any): boolean {
		return !(step.stepType === 'replace' && step.from === 0 && step.to === 0);
	}

	/**
	 * Update the steps, doc and version in the database if the stored steps are different.
	 * It does a full clear and replace with the new steps.
	 * @param version The collab version for creating unique key/value pairs in the db
	 * @param steps The steps to store in the database
	 * @returns true if the steps were updated, false if the steps were the same as the stored steps
	 */
	async updateIfChanged(
		version: number,
		baseDocJSON: JSONDocNode,
		steps: readonly Step[],
	): Promise<boolean> {
		try {
			const isLocked = (await this.syncLock.isLocked()) && !this.hasLock;
			if (isLocked) {
				return false;
			}
			const db = await this.openDB();
			const tx = db.transaction([OFFLINE_STEPS_STORE_NAME, OFFLINE_META_STORE_NAME], 'readwrite');
			const stepsStore = tx.objectStore(OFFLINE_STEPS_STORE_NAME);
			const docStore = tx.objectStore(OFFLINE_META_STORE_NAME);

			const localSteps = steps.map(this.sanitizeStep);
			const storedSteps: StepJson[] = [];

			let cursor = await stepsStore.openCursor();
			while (cursor) {
				storedSteps.push(cursor.value.step);
				cursor = await cursor.continue();
			}

			// filter out steps that create noise in the diff
			const filteredLocalSteps = localSteps.filter(this.filterEmptyReplaceStep);
			const filteredStoredSteps = storedSteps.filter(this.filterEmptyReplaceStep);
			if (isEqual(filteredLocalSteps, filteredStoredSteps)) {
				await tx.done;
				return false;
			}

			// Clear existing steps
			await stepsStore.clear();
			// Store serialized steps in the database
			for (const serializedStep of localSteps) {
				await stepsStore.add({ step: serializedStep });
			}

			// Store the doc and version
			await docStore.clear();
			await docStore.add({ doc: baseDocJSON, version }, 1);

			await tx.done;
			return true;
		} catch (e) {
			this.onError('updateIfChanged()', e);
			throw e;
		}
	}

	/**
	 * Retrieve the steps, doc and version from the database.
	 * Will open a new database connection, if one is not already open.
	 * @param pmSchema The current ProseMirror schema
	 * @returns The steps stored in the database
	 */
	async getData(pmSchema: PMSchema): Promise<GetDataResponse> {
		try {
			const db = await this.openDB();
			const tx = db.transaction([OFFLINE_STEPS_STORE_NAME, OFFLINE_META_STORE_NAME], 'readonly');
			const stepsStore = tx.objectStore(OFFLINE_STEPS_STORE_NAME);
			const docStore = tx.objectStore(OFFLINE_META_STORE_NAME);

			const storedSteps: Step[] = [];

			let cursor = await stepsStore.openCursor();
			while (cursor) {
				const stepJson = cursor.value.step;
				const step = Step.fromJSON(pmSchema, stepJson);
				storedSteps.push(step);
				cursor = await cursor.continue();
			}

			const data = await docStore.get(1);
			if (data === undefined) {
				await tx.done;
				return { steps: storedSteps, docJSON: null, version: null };
			}

			await tx.done;
			return { steps: storedSteps, docJSON: data.doc, version: data.version };
		} catch (e) {
			this.onError('getData()', e);
			throw e;
		}
	}

	/**
	 * Clear all steps, and stored doc from the database
	 * Will open a new database connection, if one is not already open.
	 */
	async clear(all?: boolean): Promise<boolean> {
		try {
			const isLocked = (await this.syncLock.isLocked()) && !this.hasLock;
			if (isLocked) {
				return false;
			}
			const db = await this.openDB();
			const tx = db.transaction([OFFLINE_STEPS_STORE_NAME, OFFLINE_META_STORE_NAME], 'readwrite');
			const stepsStore = tx.objectStore(OFFLINE_STEPS_STORE_NAME);
			const docStore = tx.objectStore(OFFLINE_META_STORE_NAME);

			await stepsStore.clear();
			if (all) {
				await docStore.clear();
			}
			await tx.done;
			return true;
		} catch (e) {
			this.onError('clear()', e);
			throw e;
		}
	}
}
